import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Pencil, Trash2, Clock, AlertTriangle, CheckCircle2, ListTodo, List, Columns3, MessageSquare, Send, Paperclip, Mic, MicOff, X, Play, Upload, Shield, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { getTasks, createTask, updateTask, deleteTask, getTaskUsers, addTaskComment, uploadTaskFile } from '../../services/taskApi';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STATUSES = [
  { id: 'todo', label: 'To Do', icon: ListTodo, color: 'bg-slate-500', border: 'border-slate-500/30' },
  { id: 'in_progress', label: 'In Progress', icon: Clock, color: 'bg-amber-500', border: 'border-amber-500/30' },
  { id: 'pending_review', label: 'Pending Review', icon: Shield, color: 'bg-violet-500', border: 'border-violet-500/30' },
  { id: 'done', label: 'Done', icon: CheckCircle2, color: 'bg-emerald-500', border: 'border-emerald-500/30' },
  { id: 'overdue', label: 'Overdue', icon: AlertTriangle, color: 'bg-red-500', border: 'border-red-500/30' },
];
const PRIORITIES = { high: 'text-red-400 bg-red-500/10', medium: 'text-amber-400 bg-amber-500/10', low: 'text-blue-400 bg-blue-500/10' };
const CATEGORIES = ['General', 'Marketing', 'Production', 'Quality', 'Sales', 'Service', 'Purchase', 'HR', 'Other'];

const TaskBoard = () => {
  const { user, isDark } = useOutletContext();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState('kanban');
  const [taskView, setTaskView] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [editing, setEditing] = useState(null);
  const [comment, setComment] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [recording, setRecording] = useState(false);
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium', status: 'todo', assigned_to: '', cc_users: [],
    deadline: '', recurring: '', tags: '', category: 'General', attachments: [], voice_note: ''
  });

  const taskAge = (iso) => {
    if (!iso) return '';
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return d <= 0 ? 'today' : `${d}d ago`;
  };

  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white hover:bg-white/8' : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50';
  const dialogCls = isDark ? 'bg-[#0c1222] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900';
  const labelCls = isDark ? 'text-[11px] text-white/50 uppercase tracking-wide' : 'text-[11px] text-gray-500 uppercase tracking-wide';
  const btnOutlineCls = isDark ? 'border-white/10 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100';

  const fetchTasks = useCallback(async () => {
    try {
      const params = {};
      if (filterPriority) params.priority = filterPriority;
      if (filterAssignee) params.assigned_to = filterAssignee;
      if (taskView !== 'all') params.view = taskView;
      const res = await getTasks(params);
      setTasks(res.data || []);
    } catch (err) { console.error('TaskBoard: fetchTasks failed', err); }
  }, [filterPriority, filterAssignee, taskView]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    const fetchUsers = async () => {
      try { const res = await getTaskUsers(); setUsers(res.data || []); } catch { setUsers([]); }
    };
    fetchUsers();
  }, []);

  const getFileUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${API_BASE}${url}`;
  };

  const openCreate = (status = 'todo') => {
    setEditing(null);
    setForm({ title: '', description: '', priority: 'medium', status, assigned_to: '', cc_users: [], deadline: '', recurring: '', tags: '', category: 'General', attachments: [], voice_note: '' });
    setDialogOpen(true);
  };

  const openEdit = (task) => {
    setEditing(task);
    setForm({
      title: task.title || '', description: task.description || '',
      priority: task.priority || 'medium', status: task.status || 'todo',
      assigned_to: task.assigned_to || '', cc_users: task.cc_users || [], deadline: task.deadline || '',
      recurring: task.recurring || '', tags: (task.tags || []).join(', '),
      category: task.category || 'General',
      attachments: task.attachments || [], voice_note: task.voice_note || ''
    });
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const res = await uploadTaskFile(file);
        uploaded.push({ url: res.data.url, name: file.name });
      }
      setForm(f => ({ ...f, attachments: [...f.attachments, ...uploaded] }));
      toast.success(`${uploaded.length} file(s) attached`);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];
      mediaRecorder.current.ondataavailable = (e) => audioChunks.current.push(e.data);
      mediaRecorder.current.onstop = async () => {
        const blob = new Blob(audioChunks.current, { type: 'audio/webm' });
        const file = new File([blob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });
        setUploading(true);
        try {
          const res = await uploadTaskFile(file);
          setForm(f => ({ ...f, voice_note: res.data.url }));
          toast.success('Voice note recorded');
        } catch { toast.error('Upload failed'); }
        finally { setUploading(false); }
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorder.current.start();
      setRecording(true);
    } catch { toast.error('Microphone access denied'); }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && recording) {
      mediaRecorder.current.stop();
      setRecording(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    const data = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
    if (!data.recurring) delete data.recurring;
    try {
      if (editing) { await updateTask(editing.id, data); toast.success('Updated'); }
      else { await createTask(data); toast.success('Created'); }
      setDialogOpen(false);
      fetchTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { submittingRef.current = false; setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try { await deleteTask(id); toast.success('Deleted'); fetchTasks(); setDetailTask(null); } catch { toast.error('Failed'); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try { await updateTask(taskId, { status: newStatus }); fetchTasks(); } catch (err) { console.error('TaskBoard: status update failed', err); }
  };

  const handleComment = async () => {
    if (!comment.trim() || !detailTask) return;
    try {
      await addTaskComment(detailTask.id, comment);
      setComment('');
      const res = await getTasks({});
      setTasks(res.data || []);
      const updated = res.data.find(t => t.id === detailTask.id);
      if (updated) setDetailTask(updated);
      toast.success('Comment added');
    } catch (err) { console.error('TaskBoard: add comment failed', err); }
  };

  const getByStatus = (statusId) => tasks.filter(t => t.status === statusId);

  const TaskCard = ({ task }) => (
    <div className={`rounded-xl p-3.5 transition-all duration-200 cursor-pointer border group ${
      isDark ? 'bg-white/[0.04] hover:bg-white/[0.07] border-white/5 hover:border-white/10' : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300 shadow-sm'
    }`} onClick={() => setDetailTask(task)} data-testid={`task-card-${task.id}`}>
      <div className="flex items-start justify-between gap-2">
        <h4 className={`text-sm font-medium flex-1 line-clamp-2 leading-snug ${isDark ? 'text-white/90' : 'text-gray-900'}`}>{task.title}</h4>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${PRIORITIES[task.priority] || PRIORITIES.medium}`}>{task.priority}</span>
      </div>
      {task.category && <span className={`text-[9px] px-2 py-0.5 rounded-md mt-2 inline-block border ${isDark ? 'bg-white/5 text-white/40 border-white/5' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>{task.category}</span>}
      {task.description && <p className={`text-xs mt-1.5 line-clamp-1 ${isDark ? 'text-white/30' : 'text-gray-500'}`}>{task.description}</p>}
      <div className={`flex items-center justify-between mt-3 pt-2 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
        <div className="flex items-center gap-2">
          {task.assigned_to_name && <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold border ${isDark ? 'bg-blue-500/15 text-blue-400 border-blue-500/10' : 'bg-blue-100 text-blue-600 border-blue-200'}`} title={task.assigned_to_name}>{task.assigned_to_name.charAt(0)}</div>}
          {task.deadline && <span className={`text-[10px] flex items-center gap-0.5 ${isDark ? 'text-white/25' : 'text-gray-400'}`}><Clock size={10} /> {task.deadline.split('T')[0]}</span>}
          {task.created_at && <span className={`text-[10px] ${isDark ? 'text-white/25' : 'text-gray-400'}`} title={`Created ${task.created_at.split('T')[0]}`} data-testid={`task-age-${task.id}`}>{taskAge(task.created_at)}</span>}
        </div>
        <div className="flex items-center gap-2">
          {task.attachments?.length > 0 && <Paperclip size={11} className={isDark ? 'text-white/20' : 'text-gray-400'} />}
          {task.voice_note && <Mic size={11} className={isDark ? 'text-white/20' : 'text-gray-400'} />}
          {task.comments?.length > 0 && <span className={`text-[10px] flex items-center gap-0.5 ${isDark ? 'text-white/20' : 'text-gray-400'}`}><MessageSquare size={10} /> {task.comments.length}</span>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="task-board">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Tasks</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select className={`h-9 rounded-lg text-xs px-3 transition-colors border ${selectCls}`} value={taskView} onChange={e => setTaskView(e.target.value)} data-testid="task-view-select">
            <option value="all">All Tasks</option>
            <option value="assigned_to_me">Assigned to Me</option>
            <option value="my_tasks">Created by Me</option>
          </select>
          <select className={`h-9 rounded-lg text-xs px-3 transition-colors border ${selectCls}`} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
            <option value="">All Priority</option>
            <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
          {users.length > 0 && (
            <select className={`h-9 rounded-lg text-xs px-3 transition-colors border ${selectCls}`} value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
              <option value="">All Assignees</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          <div className={`flex rounded-lg p-0.5 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-100 border-gray-200'}`}>
            <button onClick={() => setView('kanban')} className={`p-1.5 rounded-md transition-all ${view === 'kanban' ? 'bg-blue-500/20 text-blue-400' : isDark ? 'text-white/40 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`}><Columns3 size={16} /></button>
            <button onClick={() => setView('list')} className={`p-1.5 rounded-md transition-all ${view === 'list' ? 'bg-blue-500/20 text-blue-400' : isDark ? 'text-white/40 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`}><List size={16} /></button>
          </div>
          <Button size="sm" onClick={() => openCreate()} className="bg-blue-600 hover:bg-blue-700 h-9 px-4 rounded-lg" data-testid="create-task-btn"><Plus size={14} className="mr-1.5" /> New Task</Button>
        </div>
      </div>

      {view === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          {STATUSES.map(col => (
            <div key={col.id} className={`rounded-xl border min-h-[250px] ${col.border} ${isDark ? 'bg-white/[0.02]' : 'bg-gray-50/50'}`}>
              <div className={`p-3 flex items-center justify-between border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${col.color}`} />
                  <span className={`text-xs font-semibold ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{col.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-mono ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-200 text-gray-600'}`}>{getByStatus(col.id).length}</span>
                </div>
                <button onClick={() => openCreate(col.id)} className={`transition-colors ${isDark ? 'text-white/20 hover:text-white/60' : 'text-gray-300 hover:text-gray-500'}`}><Plus size={16} /></button>
              </div>
              <div className="p-2 space-y-2">{getByStatus(col.id).map(task => <TaskCard key={task.id} task={task} />)}</div>
            </div>
          ))}
        </div>
      )}

      {view === 'list' && (
        <div className="space-y-1.5">
          {tasks.length === 0 ? <div className={`text-center py-16 ${isDark ? 'text-white/20' : 'text-gray-400'}`}><CheckCircle2 size={40} className="mx-auto mb-3 opacity-20" /><p>No tasks found</p></div> : tasks.map(task => (
            <div key={task.id} className={`flex items-center gap-3 rounded-xl px-4 py-3 cursor-pointer border transition-all ${
              isDark ? 'bg-white/[0.03] hover:bg-white/[0.06] border-white/5 hover:border-white/10' : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300 shadow-sm'
            }`} onClick={() => setDetailTask(task)}>
              <select className={`h-7 rounded-lg text-[10px] px-1.5 border ${selectCls}`} value={task.status} onClick={e => e.stopPropagation()} onChange={e => handleStatusChange(task.id, e.target.value)}>
                {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${PRIORITIES[task.priority]}`}>{task.priority}</span>
              {task.category && <span className={`text-[9px] px-2 py-0.5 rounded-md border ${isDark ? 'bg-white/5 text-white/30 border-white/5' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>{task.category}</span>}
              <span className={`text-sm flex-1 truncate ${isDark ? 'text-white/80' : 'text-gray-800'}`}>{task.title}</span>
              {task.assigned_to_name && <span className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-500'}`}>{task.assigned_to_name}</span>}
              {task.created_at && <span className={`text-[10px] ${isDark ? 'text-white/25' : 'text-gray-400'}`} title={`Created ${task.created_at.split('T')[0]}`}>{taskAge(task.created_at)}</span>}
              {task.deadline && <span className={`text-xs font-mono ${isDark ? 'text-white/20' : 'text-gray-400'}`}>{task.deadline.split('T')[0]}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`max-w-lg max-h-[90vh] overflow-y-auto ${dialogCls}`}>
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>{editing ? 'Edit' : 'New'} Task</DialogTitle><DialogDescription className={isDark ? 'text-white/40' : 'text-gray-500'}>Fill in the task details</DialogDescription></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className={labelCls}>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className={`${inputCls} h-10 rounded-lg mt-1`} data-testid="task-title-input" /></div>
            <div><Label className={labelCls}>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className={`${inputCls} rounded-lg mt-1`} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className={`${labelCls} mb-1 block`}>Priority</Label>
                <select className={`w-full h-10 rounded-lg text-sm px-3 border ${selectCls}`} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                  <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select></div>
              <div><Label className={`${labelCls} mb-1 block`}>Status</Label>
                <select className={`w-full h-10 rounded-lg text-sm px-3 border ${selectCls}`} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select></div>
              <div><Label className={`${labelCls} mb-1 block`}>Category</Label>
                <select className={`w-full h-10 rounded-lg text-sm px-3 border ${selectCls}`} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className={`${labelCls} mb-1 block`}>Assign To</Label>
                <select className={`w-full h-10 rounded-lg text-sm px-3 border ${selectCls}`} value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} data-testid="task-assign-select">
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select></div>
              <div><Label className={`${labelCls} mb-1 block`}>Deadline</Label><Input type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} className={`${inputCls} h-10 rounded-lg`} /></div>
            </div>
            <div>
              <Label className={`${labelCls} mb-1 block`}>CC (notify others by email)</Label>
              <div className={`max-h-28 overflow-y-auto rounded-lg border p-2 grid grid-cols-2 gap-1 ${isDark ? 'border-white/10 bg-white/[0.03]' : 'border-gray-200 bg-gray-50'}`} data-testid="task-cc-list">
                {users.filter(u => u.id !== form.assigned_to).map(u => (
                  <label key={u.id} className={`flex items-center gap-1.5 text-xs cursor-pointer truncate ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                    <input
                      type="checkbox"
                      checked={form.cc_users.includes(u.id)}
                      onChange={e => setForm(f => ({ ...f, cc_users: e.target.checked ? [...f.cc_users, u.id] : f.cc_users.filter(id => id !== u.id) }))}
                      data-testid={`task-cc-${u.id}`}
                    />
                    <span className="truncate">{u.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className={`${labelCls} mb-1 block`}>Recurring</Label>
                <select className={`w-full h-10 rounded-lg text-sm px-3 border ${selectCls}`} value={form.recurring} onChange={e => setForm({ ...form, recurring: e.target.value })}>
                  <option value="">Once</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                </select></div>
              <div><Label className={`${labelCls} mb-1 block`}>Tags (comma)</Label><Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} className={`${inputCls} h-10 rounded-lg`} placeholder="urgent, follow-up" /></div>
            </div>
            <div>
              <Label className={isDark ? 'text-white/70' : 'text-gray-600'}>Attachments</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {form.attachments.map((a, i) => (
                  <div key={a.url || `att-${i}`} className={`flex items-center gap-1.5 rounded px-2 py-1 text-xs ${isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-600'}`}>
                    <Paperclip size={10} /> {a.name || `File ${i+1}`}
                    <button type="button" onClick={() => setForm(f => ({ ...f, attachments: f.attachments.filter((_, idx) => idx !== i) }))} className="text-red-400 hover:text-red-300"><X size={12} /></button>
                  </div>
                ))}
                <label className={`cursor-pointer flex items-center gap-1.5 rounded px-3 py-1.5 text-xs transition-colors ${isDark ? 'bg-white/5 hover:bg-white/10 text-white/50' : 'bg-gray-100 hover:bg-gray-200 text-gray-500'}`}>
                  <Upload size={12} /> {uploading ? 'Uploading...' : 'Attach Files'}
                  <input type="file" multiple className="hidden" onChange={handleFileUpload} disabled={uploading} />
                </label>
              </div>
            </div>
            <div>
              <Label className={isDark ? 'text-white/70' : 'text-gray-600'}>Voice Note</Label>
              <div className="flex items-center gap-2 mt-1">
                {form.voice_note ? (
                  <div className={`flex items-center gap-2 rounded px-3 py-1.5 flex-1 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <Play size={12} className="text-green-400" />
                    <audio src={getFileUrl(form.voice_note)} controls className="h-7 flex-1" style={{maxWidth: '200px'}} />
                    <button type="button" onClick={() => setForm(f => ({ ...f, voice_note: '' }))} className="text-red-400"><X size={14} /></button>
                  </div>
                ) : recording ? (
                  <Button type="button" size="sm" variant="destructive" onClick={stopRecording} className="h-8"><MicOff size={14} className="mr-1" /> Stop Recording</Button>
                ) : (
                  <Button type="button" size="sm" variant="outline" onClick={startRecording} className={`h-8 ${btnOutlineCls}`}><Mic size={14} className="mr-1" /> Record Voice</Button>
                )}
              </div>
            </div>
            <div className={`flex gap-3 pt-3 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 h-11 rounded-lg font-medium" data-testid="task-submit-btn" disabled={uploading || submitting}>{submitting ? 'Saving…' : (editing ? 'Update Task' : 'Create Task')}</Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className={`h-11 px-6 rounded-lg ${btnOutlineCls}`}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailTask} onOpenChange={() => setDetailTask(null)}>
        <DialogContent className={`max-w-lg max-h-[85vh] overflow-y-auto ${dialogCls}`}>
          {detailTask && (<>
            <DialogHeader>
              <div className="flex items-start justify-between gap-2">
                <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>{detailTask.title}</DialogTitle>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${PRIORITIES[detailTask.priority]}`}>{detailTask.priority}</span>
              </div>
              <DialogDescription className={isDark ? 'text-white/40' : 'text-gray-500'}>{detailTask.category || 'General'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {detailTask.description && <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{detailTask.description}</p>}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Status</span><p className={`capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>{detailTask.status.replace('_', ' ')}</p></div>
                <div><span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Assigned To</span><p className={isDark ? 'text-white' : 'text-gray-900'}>{detailTask.assigned_to_name || 'Unassigned'}</p></div>
                {detailTask.deadline && <div><span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Deadline</span><p className={isDark ? 'text-white' : 'text-gray-900'}>{detailTask.deadline.split('T')[0]}</p></div>}
                {detailTask.recurring && <div><span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Recurring</span><p className={`capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>{detailTask.recurring}</p></div>}
                <div><span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Created By</span><p className={isDark ? 'text-white' : 'text-gray-900'}>{detailTask.created_by_name}</p></div>
                {detailTask.created_at && (
                  <div data-testid="task-created-on"><span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Created On</span>
                    <p className={isDark ? 'text-white' : 'text-gray-900'}>{detailTask.created_at.split('T')[0]} <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>({taskAge(detailTask.created_at)})</span></p>
                  </div>
                )}
                {detailTask.cc_info?.length > 0 && (
                  <div className="col-span-2"><span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>CC</span>
                    <p className={`text-xs ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{detailTask.cc_info.map(c => c.name).join(', ')}</p>
                  </div>
                )}
              </div>
              {detailTask.attachments?.length > 0 && (
                <div><span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Attachments</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">{detailTask.attachments.map((a, i) => (
                    <a key={a.url || `det-att-${i}`} href={getFileUrl(a.url)} target="_blank" rel="noreferrer" className={`text-[10px] text-blue-400 px-2 py-0.5 rounded hover:bg-white/20 ${isDark ? 'bg-white/10' : 'bg-blue-50'}`}><Paperclip size={10} className="inline mr-0.5" />{a.name || `File ${i+1}`}</a>
                  ))}</div>
                </div>
              )}
              {detailTask.voice_note && (
                <div><span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Voice Note</span><audio src={getFileUrl(detailTask.voice_note)} controls className="mt-1 w-full h-8" /></div>
              )}
              <div className="flex gap-2 pt-2 flex-wrap">
                {detailTask.status === 'todo' && detailTask.assigned_to === user.id && (
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 h-7 text-xs" onClick={async () => { await updateTask(detailTask.id, { status: 'in_progress' }); fetchTasks(); setDetailTask(null); toast.success('Started working!'); }} data-testid="start-working-btn">
                    <Clock size={12} className="mr-1" /> Start Working
                  </Button>
                )}
                {detailTask.status === 'in_progress' && detailTask.assigned_to === user.id && (
                  <Button size="sm" className="bg-violet-600 hover:bg-violet-700 h-7 text-xs" onClick={async () => { await updateTask(detailTask.id, { status: 'pending_review' }); fetchTasks(); setDetailTask(null); toast.success('Submitted for review'); }} data-testid="submit-review-btn">
                    <Shield size={12} className="mr-1" /> Submit for Review
                  </Button>
                )}
                {detailTask.status !== 'done' && detailTask.status !== 'pending_review' && detailTask.status !== 'todo' && detailTask.assigned_to === user.id && detailTask.status !== 'in_progress' && (
                  <Button size="sm" className="bg-violet-600 hover:bg-violet-700 h-7 text-xs" onClick={async () => { await updateTask(detailTask.id, { status: 'pending_review' }); fetchTasks(); setDetailTask(null); toast.success('Submitted for review'); }}>
                    <Shield size={12} className="mr-1" /> Submit for Review
                  </Button>
                )}
                {detailTask.status === 'pending_review' && (detailTask.created_by === user.id || user.role === 'admin') && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={async () => { await updateTask(detailTask.id, { status: 'done' }); fetchTasks(); setDetailTask(null); toast.success('Task verified & done!'); }} data-testid="verify-done-btn">
                    <CheckCircle2 size={12} className="mr-1" /> Verify Done
                  </Button>
                )}
                {detailTask.status === 'pending_review' && (detailTask.created_by === user.id || user.role === 'admin') && (
                  <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 h-7 text-xs" onClick={async () => { await updateTask(detailTask.id, { status: 'in_progress' }); fetchTasks(); setDetailTask(null); toast.info('Task reopened'); }} data-testid="reopen-task-btn">
                    <RotateCcw size={12} className="mr-1" /> Reopen
                  </Button>
                )}
                {detailTask.status !== 'done' && detailTask.status !== 'pending_review' && (detailTask.created_by === user.id || user.role === 'admin') && detailTask.assigned_to !== user.id && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={async () => { await updateTask(detailTask.id, { status: 'done' }); fetchTasks(); setDetailTask(null); toast.success('Marked as done'); }} data-testid="mark-done-btn">
                    <CheckCircle2 size={12} className="mr-1" /> Mark Done
                  </Button>
                )}
                {(detailTask.created_by === user.id || user.role === 'admin') && (
                  <Button size="sm" variant="outline" className={`h-7 text-xs ${btnOutlineCls}`} onClick={() => { openEdit(detailTask); setDetailTask(null); }}><Pencil size={12} className="mr-1" /> Edit</Button>
                )}
                {(detailTask.created_by === user.id || user.role === 'admin') && (
                  <Button size="sm" variant="outline" className="border-red-500/20 text-red-400 hover:bg-red-500/10 h-7 text-xs" onClick={() => handleDelete(detailTask.id)}><Trash2 size={12} className="mr-1" /> Delete</Button>
                )}
              </div>
              <div className={`border-t pt-3 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Comments ({detailTask.comments?.length || 0})</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                  {(detailTask.comments || []).map(c => (
                    <div key={c.id} className={`rounded-lg p-2.5 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-blue-400">{c.user_name}</span>
                        <span className={`text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{new Date(c.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className={`text-xs ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{c.text}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={comment} onChange={e => setComment(e.target.value)} placeholder="Add a comment..." className={`${inputCls} text-sm flex-1`} onKeyDown={e => e.key === 'Enter' && handleComment()} />
                  <Button size="sm" onClick={handleComment} className="bg-blue-600 hover:bg-blue-700 px-3"><Send size={14} /></Button>
                </div>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskBoard;
